/**
 * Enemy & boss definitions. Behaviour is data-driven: each enemy has a movement
 * style and a list of telegraphed attacks that the generic AI chooses from.
 */
import type { ProjectileId, SpriteId } from '../art/pixel/types';
import type { MaterialId } from '../game/types';
import { ACT2_ENEMIES } from './enemies-act2';

export type StatusKind = 'burn' | 'poison' | 'slow' | 'freeze';

export interface StatusSpec {
  kind: StatusKind;
  duration: number;
  /** DoT: fraction of attacker power per second. Slow: speed reduction fraction. */
  power?: number;
  chance?: number;
}

export interface ProjectileSpec {
  sprite: ProjectileId;
  speed: number;
  radius: number;
  dmg: number;
  life?: number;
  /** Radians per second of turning toward the target. */
  homing?: number;
  status?: StatusSpec;
  /** Explodes on impact / expiry. */
  explode?: { radius: number; dmg: number; status?: StatusSpec };
  /** Size multiplier for drawing. */
  scale?: number;
  /** Leaves a burning / freezing puddle on expiry. */
  puddle?: { radius: number; duration: number; dmg: number; status?: StatusSpec };
}

interface AttackBase {
  /** Seconds of telegraph before the attack fires. */
  windup: number;
  /** Seconds of vulnerability after. */
  recover: number;
  cooldown: number;
  /** Max distance to the player to consider this attack. */
  range: number;
  minRange?: number;
  weight?: number;
  /** Damage multiplier of the enemy's attack stat. */
  dmg: number;
  /** Boss phase (0-based) required to use this attack. */
  phase?: number;
  /** Stop using once the boss reaches this phase. */
  maxPhase?: number;
  status?: StatusSpec;
  sfx?: import('../audio').SfxId;
  /** Shout text shown when used (bosses). */
  shout?: string;
}

export type AttackDef = AttackBase &
  (
    | { type: 'melee'; arc: number; reach: number; repeat?: number }
    | { type: 'lunge'; speed: number; duration: number; repeat?: number }
    | { type: 'charge'; speed: number; length: number }
    | {
        type: 'shoot';
        proj: ProjectileSpec;
        count: number;
        spread: number;
        bursts?: number;
        burstDelay?: number;
        aimJitter?: number;
      }
    | {
        type: 'ring';
        proj: ProjectileSpec;
        count: number;
        waves?: number;
        waveDelay?: number;
        rotate?: number;
      }
    | { type: 'spiral'; proj: ProjectileSpec; arms: number; shots: number; interval: number; turn: number }
    | { type: 'nova'; radius: number; selfDestruct?: boolean }
    | {
        type: 'aoe';
        at: 'player' | 'around' | 'line' | 'cross';
        radius: number;
        count: number;
        spread: number;
        delay: number;
        stagger?: number;
        persist?: number;
      }
    | { type: 'line'; length: number; width: number; persist?: number }
    | { type: 'cone'; length: number; arc: number; duration: number; sweep?: number }
    | { type: 'beam'; length: number; width: number; duration: number; sweep: number }
    | { type: 'summon'; enemy: string; count: number; max: number }
    | { type: 'teleport'; near: 'player' | 'random'; novaRadius?: number }
    | { type: 'dive'; radius: number; airTime: number }
  );

export type Behavior = 'melee' | 'ranged' | 'hopper' | 'flyer' | 'caster' | 'kamikaze' | 'turret' | 'boss';

export interface EnemyDef {
  id: string;
  name: string;
  sprite: SpriteId;
  hp: number;
  atk: number;
  def: number;
  speed: number;
  radius: number;
  /** Knockback resistance 0..1. */
  mass: number;
  xp: number;
  gold: number;
  behavior: Behavior;
  attacks: AttackDef[];
  aggro: number;
  flying?: boolean;
  scale?: number;
  /** Damage reduction when hit from the front while not attacking. */
  guard?: number;
  onDeath?: {
    split?: { enemy: string; count: number };
    explode?: { radius: number; dmg: number; status?: StatusSpec };
  };
  drops?: { material: MaterialId; chance: number }[];
  element?: 'fire' | 'ice' | 'void' | 'nature' | 'earth';
  immune?: StatusKind[];
  lore: string;
  /** Preferred distance for ranged/caster behaviours. */
  keepAway?: number;
  /** Bosses only. */
  boss?: BossInfo;
}

export interface BossPhase {
  /** Enter this phase at or below this HP fraction. */
  at: number;
  speedMult: number;
  cooldownMult: number;
  shout?: string;
  summon?: { enemy: string; count: number };
}

export interface BossInfo {
  title: string;
  phases: BossPhase[];
  music: import('../audio').MusicId;
  /** Portrait for the VS splash (humanoid bosses). */
  portrait?: import('../art/anime').PortraitId;
  shardName?: string;
}

const P = {
  arrow: { sprite: 'proj_arrow', speed: 170, radius: 3, dmg: 1 },
  seed: { sprite: 'proj_seed', speed: 120, radius: 3, dmg: 0.8 },
  rock: { sprite: 'proj_rock', speed: 130, radius: 4, dmg: 1.2 },
  fireball: {
    sprite: 'proj_fireball',
    speed: 120,
    radius: 4,
    dmg: 1.1,
    status: { kind: 'burn', duration: 3, power: 0.15, chance: 0.5 },
  },
  iceshard: {
    sprite: 'proj_iceshard',
    speed: 150,
    radius: 3,
    dmg: 0.9,
    status: { kind: 'slow', duration: 2, power: 0.35, chance: 0.6 },
  },
  snowball: { sprite: 'proj_snowball', speed: 125, radius: 4, dmg: 1 },
  magic: { sprite: 'proj_magic', speed: 110, radius: 3, dmg: 0.9 },
  void: { sprite: 'proj_void', speed: 70, radius: 5, dmg: 1.2, homing: 1.6, life: 5 },
  crystal: { sprite: 'proj_crystal', speed: 140, radius: 3, dmg: 1 },
} satisfies Record<string, ProjectileSpec>;

const E: EnemyDef[] = [
  // ------------------------------------------------------------ forest ----
  {
    id: 'slime',
    name: 'Green Slime',
    sprite: 'slime',
    hp: 30,
    atk: 8,
    def: 2,
    speed: 30,
    radius: 6,
    mass: 0.1,
    xp: 6,
    gold: 3,
    behavior: 'hopper',
    aggro: 90,
    element: 'nature',
    attacks: [
      {
        type: 'lunge',
        speed: 150,
        duration: 0.28,
        windup: 0.5,
        recover: 0.5,
        cooldown: 1.4,
        range: 50,
        dmg: 1,
      },
    ],
    drops: [{ material: 'herb', chance: 0.08 }],
    lore: 'A wobbling mass of forest dew given hunger by a shard’s touch. Harmless alone. Rarely alone.',
  },
  {
    id: 'wolf',
    name: 'Shardfang Wolf',
    sprite: 'wolf',
    hp: 40,
    atk: 11,
    def: 3,
    speed: 64,
    radius: 7,
    mass: 0.25,
    xp: 9,
    gold: 4,
    behavior: 'melee',
    aggro: 120,
    attacks: [
      {
        type: 'lunge',
        speed: 240,
        duration: 0.3,
        windup: 0.55,
        recover: 0.6,
        cooldown: 2.0,
        range: 75,
        minRange: 20,
        dmg: 1.2,
      },
    ],
    drops: [{ material: 'pelt', chance: 0.25 }],
    lore: 'Its eyes glow the same cyan as the fallen shards. Packs circle their prey before the lunge.',
  },
  {
    id: 'mushroom',
    name: 'Sporecap',
    sprite: 'mushroom',
    hp: 46,
    atk: 9,
    def: 4,
    speed: 22,
    radius: 7,
    mass: 0.4,
    xp: 8,
    gold: 3,
    behavior: 'melee',
    aggro: 80,
    element: 'nature',
    attacks: [
      {
        type: 'nova',
        radius: 32,
        windup: 0.8,
        recover: 0.6,
        cooldown: 2.8,
        range: 34,
        dmg: 0.9,
        status: { kind: 'poison', duration: 4, power: 0.2 },
        sfx: 'void_pulse',
      },
    ],
    drops: [{ material: 'herb', chance: 0.3 }],
    lore: 'Releases choking spores when threatened. Keep your distance, or strike and roll away.',
  },
  {
    id: 'goblin',
    name: 'Goblin Archer',
    sprite: 'goblin',
    hp: 32,
    atk: 10,
    def: 2,
    speed: 50,
    radius: 6,
    mass: 0.2,
    xp: 10,
    gold: 6,
    behavior: 'ranged',
    keepAway: 90,
    aggro: 150,
    attacks: [
      {
        type: 'shoot',
        proj: P.arrow,
        count: 1,
        spread: 0,
        windup: 0.6,
        recover: 0.4,
        cooldown: 1.9,
        range: 170,
        dmg: 1,
        sfx: 'arrow_shoot',
      },
    ],
    lore: 'Opportunists who followed the falling stars hoping for treasure. They found bows instead.',
  },
  {
    id: 'sapling',
    name: 'Thornling',
    sprite: 'sapling',
    hp: 22,
    atk: 8,
    def: 3,
    speed: 44,
    radius: 6,
    mass: 0.1,
    xp: 2,
    gold: 0,
    behavior: 'melee',
    aggro: 300,
    element: 'nature',
    attacks: [
      {
        type: 'lunge',
        speed: 160,
        duration: 0.25,
        windup: 0.45,
        recover: 0.4,
        cooldown: 1.5,
        range: 40,
        dmg: 0.8,
      },
    ],
    lore: 'Seedlings spat out by Thornmaw. They grow up fast, and angry.',
  },
  // ------------------------------------------------------------- caves ----
  {
    id: 'bat',
    name: 'Cave Bat',
    sprite: 'bat',
    hp: 24,
    atk: 12,
    def: 2,
    speed: 72,
    radius: 5,
    mass: 0.1,
    xp: 8,
    gold: 3,
    behavior: 'flyer',
    flying: true,
    aggro: 130,
    attacks: [
      {
        type: 'lunge',
        speed: 210,
        duration: 0.3,
        windup: 0.4,
        recover: 0.5,
        cooldown: 1.7,
        range: 65,
        dmg: 1,
      },
    ],
    lore: 'Blind, but drawn to the hum of aether in your blood.',
  },
  {
    id: 'slime_crystal',
    name: 'Crystal Slime',
    sprite: 'slime_crystal',
    hp: 60,
    atk: 14,
    def: 8,
    speed: 28,
    radius: 7,
    mass: 0.3,
    xp: 12,
    gold: 6,
    behavior: 'hopper',
    aggro: 100,
    element: 'earth',
    attacks: [
      {
        type: 'lunge',
        speed: 150,
        duration: 0.3,
        windup: 0.55,
        recover: 0.5,
        cooldown: 1.6,
        range: 55,
        dmg: 1.1,
      },
    ],
    onDeath: { split: { enemy: 'shardling', count: 2 } },
    drops: [{ material: 'crystal', chance: 0.2 }],
    lore: 'Slime that swallowed a shard fragment. Cracking it open only makes more of it.',
  },
  {
    id: 'shardling',
    name: 'Shardling',
    sprite: 'slime_crystal',
    hp: 18,
    atk: 9,
    def: 4,
    speed: 44,
    radius: 5,
    mass: 0.1,
    xp: 3,
    gold: 1,
    scale: 0.65,
    behavior: 'hopper',
    aggro: 200,
    attacks: [
      {
        type: 'lunge',
        speed: 170,
        duration: 0.25,
        windup: 0.4,
        recover: 0.4,
        cooldown: 1.2,
        range: 45,
        dmg: 0.8,
      },
    ],
    lore: 'A crystal slime’s shattered remnants. Still bitter about it.',
  },
  {
    id: 'skeleton',
    name: 'Restless Guard',
    sprite: 'skeleton',
    hp: 72,
    atk: 15,
    def: 10,
    speed: 40,
    radius: 7,
    mass: 0.45,
    xp: 14,
    gold: 8,
    behavior: 'melee',
    guard: 0.6,
    aggro: 110,
    attacks: [
      {
        type: 'melee',
        arc: 2.2,
        reach: 28,
        windup: 0.5,
        recover: 0.6,
        cooldown: 1.4,
        range: 30,
        dmg: 1.2,
        sfx: 'swing',
      },
      {
        type: 'lunge',
        speed: 190,
        duration: 0.3,
        windup: 0.6,
        recover: 0.7,
        cooldown: 3.5,
        range: 80,
        minRange: 40,
        dmg: 1.1,
      },
    ],
    lore: 'Miners who never left. Their shields block frontal blows — strike after they swing, or from behind.',
  },
  {
    id: 'golem',
    name: 'Stone Golem',
    sprite: 'golem',
    hp: 140,
    atk: 20,
    def: 14,
    speed: 26,
    radius: 10,
    mass: 0.85,
    xp: 22,
    gold: 12,
    behavior: 'melee',
    aggro: 110,
    element: 'earth',
    attacks: [
      { type: 'nova', radius: 42, windup: 0.9, recover: 0.8, cooldown: 3, range: 40, dmg: 1.6, sfx: 'slam' },
      {
        type: 'shoot',
        proj: P.rock,
        count: 1,
        spread: 0,
        windup: 0.7,
        recover: 0.6,
        cooldown: 3.5,
        range: 150,
        minRange: 60,
        dmg: 1.2,
      },
    ],
    drops: [{ material: 'crystal', chance: 0.35 }],
    lore: 'Animated by a shard lodged deep in its chest. Slow to anger, slower to stop.',
  },
  // ----------------------------------------------------------- volcano ----
  {
    id: 'fire_imp',
    name: 'Cinder Imp',
    sprite: 'fire_imp',
    hp: 50,
    atk: 16,
    def: 6,
    speed: 52,
    radius: 6,
    mass: 0.2,
    xp: 15,
    gold: 8,
    behavior: 'caster',
    keepAway: 100,
    flying: true,
    aggro: 150,
    element: 'fire',
    immune: ['burn'],
    attacks: [
      {
        type: 'shoot',
        proj: P.fireball,
        count: 1,
        spread: 0,
        windup: 0.6,
        recover: 0.4,
        cooldown: 2,
        range: 180,
        dmg: 1.1,
        sfx: 'fireball',
      },
      {
        type: 'teleport',
        near: 'random',
        windup: 0.4,
        recover: 0.3,
        cooldown: 5,
        range: 60,
        dmg: 0,
        weight: 0.6,
      },
    ],
    drops: [{ material: 'ember', chance: 0.25 }],
    lore: 'Mischievous flame spirits. They blink away the moment you get close.',
  },
  {
    id: 'slime_magma',
    name: 'Magma Slime',
    sprite: 'slime_magma',
    hp: 80,
    atk: 17,
    def: 10,
    speed: 30,
    radius: 7,
    mass: 0.35,
    xp: 16,
    gold: 8,
    behavior: 'hopper',
    aggro: 100,
    element: 'fire',
    immune: ['burn'],
    attacks: [
      {
        type: 'lunge',
        speed: 150,
        duration: 0.3,
        windup: 0.55,
        recover: 0.5,
        cooldown: 1.6,
        range: 55,
        dmg: 1.1,
      },
    ],
    onDeath: { explode: { radius: 30, dmg: 1.2, status: { kind: 'burn', duration: 3, power: 0.2 } } },
    drops: [{ material: 'ember', chance: 0.2 }],
    lore: 'Bursts when slain. Finish it with a skill from range, or roll clear.',
  },
  {
    id: 'salamander',
    name: 'Salamander',
    sprite: 'salamander',
    hp: 95,
    atk: 19,
    def: 12,
    speed: 54,
    radius: 8,
    mass: 0.5,
    xp: 18,
    gold: 9,
    behavior: 'melee',
    aggro: 130,
    element: 'fire',
    immune: ['burn'],
    attacks: [
      {
        type: 'charge',
        speed: 270,
        length: 130,
        windup: 0.75,
        recover: 0.8,
        cooldown: 3.2,
        range: 130,
        minRange: 40,
        dmg: 1.4,
        status: { kind: 'burn', duration: 2, power: 0.2 },
      },
      { type: 'melee', arc: 1.8, reach: 26, windup: 0.4, recover: 0.5, cooldown: 1.3, range: 28, dmg: 1 },
    ],
    lore: 'It charges in straight lines. Sidestep, then punish its long recovery.',
  },
  {
    id: 'ember_wisp',
    name: 'Ember Wisp',
    sprite: 'ember_wisp',
    hp: 34,
    atk: 22,
    def: 4,
    speed: 76,
    radius: 5,
    mass: 0.1,
    xp: 12,
    gold: 5,
    behavior: 'kamikaze',
    flying: true,
    aggro: 140,
    element: 'fire',
    immune: ['burn'],
    attacks: [
      {
        type: 'nova',
        radius: 36,
        windup: 0.7,
        recover: 0,
        cooldown: 1,
        range: 26,
        dmg: 1.5,
        selfDestruct: true,
        status: { kind: 'burn', duration: 3, power: 0.2 },
        sfx: 'explosion',
      },
    ],
    lore: 'A flicker of hateful flame that seeks warmth — yours — and detonates.',
  },
  // ------------------------------------------------------------ tundra ----
  {
    id: 'wolf_ice',
    name: 'Rime Wolf',
    sprite: 'wolf_ice',
    hp: 75,
    atk: 20,
    def: 10,
    speed: 72,
    radius: 7,
    mass: 0.3,
    xp: 18,
    gold: 8,
    behavior: 'melee',
    aggro: 140,
    element: 'ice',
    immune: ['freeze', 'slow'],
    attacks: [
      {
        type: 'lunge',
        speed: 260,
        duration: 0.3,
        windup: 0.5,
        recover: 0.55,
        cooldown: 1.8,
        range: 80,
        minRange: 20,
        dmg: 1.2,
        status: { kind: 'slow', duration: 1.5, power: 0.3 },
      },
    ],
    drops: [{ material: 'pelt', chance: 0.3 }],
    lore: 'Hunts in packs across the Frostveil. Its bite chills to the bone.',
  },
  {
    id: 'yeti',
    name: 'Frostveil Yeti',
    sprite: 'yeti',
    hp: 210,
    atk: 26,
    def: 16,
    speed: 36,
    radius: 10,
    mass: 0.85,
    xp: 30,
    gold: 16,
    behavior: 'melee',
    aggro: 120,
    element: 'ice',
    immune: ['freeze'],
    attacks: [
      {
        type: 'melee',
        arc: 2.8,
        reach: 32,
        windup: 0.65,
        recover: 0.7,
        cooldown: 1.8,
        range: 34,
        dmg: 1.6,
        sfx: 'swing_heavy',
      },
      {
        type: 'shoot',
        proj: P.snowball,
        count: 3,
        spread: 0.5,
        windup: 0.75,
        recover: 0.6,
        cooldown: 3.2,
        range: 160,
        minRange: 50,
        dmg: 1.1,
      },
    ],
    drops: [
      { material: 'frost', chance: 0.3 },
      { material: 'pelt', chance: 0.4 },
    ],
    lore: 'Massive, territorial, and surprisingly accurate with snowballs.',
  },
  {
    id: 'frost_wraith',
    name: 'Frost Wraith',
    sprite: 'frost_wraith',
    hp: 85,
    atk: 22,
    def: 10,
    speed: 46,
    radius: 7,
    mass: 0.2,
    xp: 22,
    gold: 10,
    behavior: 'caster',
    keepAway: 110,
    flying: true,
    aggro: 160,
    element: 'ice',
    immune: ['freeze', 'slow'],
    attacks: [
      {
        type: 'shoot',
        proj: P.iceshard,
        count: 3,
        spread: 0.45,
        windup: 0.6,
        recover: 0.4,
        cooldown: 2.2,
        range: 190,
        dmg: 1,
        sfx: 'ice_shard',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 18,
        count: 3,
        spread: 30,
        delay: 0.9,
        windup: 0.5,
        recover: 0.5,
        cooldown: 4.5,
        range: 190,
        dmg: 1.3,
        status: { kind: 'freeze', duration: 1 },
        sfx: 'frost_nova',
      },
      {
        type: 'teleport',
        near: 'random',
        windup: 0.4,
        recover: 0.3,
        cooldown: 6,
        range: 70,
        dmg: 0,
        weight: 0.5,
      },
    ],
    drops: [{ material: 'frost', chance: 0.25 }],
    lore: 'Echoes of knights who followed Seraphine into the cold.',
  },
  {
    id: 'golem_ice',
    name: 'Glacier Golem',
    sprite: 'golem_ice',
    hp: 240,
    atk: 28,
    def: 20,
    speed: 26,
    radius: 10,
    mass: 0.9,
    xp: 34,
    gold: 18,
    behavior: 'melee',
    aggro: 110,
    element: 'ice',
    immune: ['freeze', 'slow'],
    attacks: [
      {
        type: 'nova',
        radius: 46,
        windup: 0.9,
        recover: 0.8,
        cooldown: 3,
        range: 44,
        dmg: 1.6,
        status: { kind: 'slow', duration: 2, power: 0.4 },
        sfx: 'slam',
      },
      {
        type: 'aoe',
        at: 'line',
        radius: 14,
        count: 6,
        spread: 22,
        delay: 0.6,
        stagger: 0.12,
        windup: 0.6,
        recover: 0.8,
        cooldown: 4,
        range: 150,
        dmg: 1.3,
        sfx: 'ice_shard',
      },
    ],
    drops: [{ material: 'frost', chance: 0.4 }],
    lore: 'Ancient ice given purpose. Its footsteps crack the tundra.',
  },
  // ----------------------------------------------------------- citadel ----
  {
    id: 'shadow_knight',
    name: 'Hollow Knight',
    sprite: 'shadow_knight',
    hp: 190,
    atk: 30,
    def: 22,
    speed: 52,
    radius: 8,
    mass: 0.6,
    xp: 38,
    gold: 20,
    behavior: 'melee',
    guard: 0.5,
    aggro: 140,
    element: 'void',
    attacks: [
      {
        type: 'melee',
        arc: 2.4,
        reach: 30,
        windup: 0.45,
        recover: 0.5,
        cooldown: 1.6,
        range: 32,
        dmg: 1.2,
        repeat: 2,
        sfx: 'swing_heavy',
      },
      {
        type: 'lunge',
        speed: 250,
        duration: 0.3,
        windup: 0.55,
        recover: 0.6,
        cooldown: 3,
        range: 100,
        minRange: 40,
        dmg: 1.3,
      },
    ],
    drops: [{ material: 'void', chance: 0.25 }],
    lore: 'Malachar’s sworn. There is nothing inside the armor but hunger.',
  },
  {
    id: 'void_mage',
    name: 'Void Acolyte',
    sprite: 'void_mage',
    hp: 120,
    atk: 28,
    def: 12,
    speed: 44,
    radius: 7,
    mass: 0.25,
    xp: 34,
    gold: 18,
    behavior: 'caster',
    keepAway: 120,
    aggro: 170,
    element: 'void',
    attacks: [
      {
        type: 'shoot',
        proj: P.void,
        count: 2,
        spread: 0.8,
        windup: 0.7,
        recover: 0.4,
        cooldown: 3,
        range: 200,
        dmg: 1.1,
        sfx: 'void_pulse',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 20,
        count: 2,
        spread: 36,
        delay: 1,
        windup: 0.5,
        recover: 0.4,
        cooldown: 4.5,
        range: 200,
        dmg: 1.4,
        sfx: 'void_pulse',
      },
      {
        type: 'teleport',
        near: 'random',
        windup: 0.35,
        recover: 0.3,
        cooldown: 5,
        range: 70,
        dmg: 0,
        weight: 0.6,
      },
    ],
    drops: [{ material: 'void', chance: 0.3 }],
    lore: 'Scholars who stared into the void until it answered.',
  },
  {
    id: 'hollow_wisp',
    name: 'Hollow Wisp',
    sprite: 'hollow_wisp',
    hp: 50,
    atk: 30,
    def: 6,
    speed: 80,
    radius: 5,
    mass: 0.1,
    xp: 16,
    gold: 6,
    behavior: 'kamikaze',
    flying: true,
    aggro: 160,
    element: 'void',
    attacks: [
      {
        type: 'nova',
        radius: 38,
        windup: 0.65,
        recover: 0,
        cooldown: 1,
        range: 26,
        dmg: 1.5,
        selfDestruct: true,
        sfx: 'void_pulse',
      },
    ],
    lore: 'Fragments of devoured souls, desperate to be whole again.',
  },
  {
    id: 'gargoyle',
    name: 'Gargoyle',
    sprite: 'gargoyle',
    hp: 170,
    atk: 30,
    def: 24,
    speed: 58,
    radius: 9,
    mass: 0.7,
    xp: 40,
    gold: 22,
    behavior: 'flyer',
    flying: true,
    aggro: 150,
    element: 'earth',
    attacks: [
      {
        type: 'lunge',
        speed: 230,
        duration: 0.35,
        windup: 0.5,
        recover: 0.6,
        cooldown: 2.2,
        range: 90,
        dmg: 1.3,
      },
      { type: 'nova', radius: 40, windup: 0.8, recover: 0.7, cooldown: 4, range: 36, dmg: 1.4, sfx: 'slam' },
    ],
    lore: 'Stone sentinels of the Sky Citadel, woken by their master’s return.',
  },
  // ------------------------------------------------------------- bosses ----
  {
    id: 'thornmaw',
    name: 'Thornmaw',
    sprite: 'boss_thornmaw',
    hp: 1150,
    atk: 12,
    def: 8,
    speed: 0,
    radius: 18,
    mass: 1,
    xp: 230,
    gold: 250,
    behavior: 'turret',
    aggro: 999,
    element: 'nature',
    immune: ['freeze'],
    boss: {
      title: 'The Devouring Bloom',
      music: 'boss',
      shardName: 'Verdant Shard',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.5,
          speedMult: 1,
          cooldownMult: 0.75,
          shout: 'GRRRAAAAH!',
          summon: { enemy: 'sapling', count: 3 },
        },
      ],
    },
    attacks: [
      {
        type: 'line',
        length: 170,
        width: 14,
        windup: 0.8,
        recover: 0.6,
        cooldown: 3,
        range: 400,
        dmg: 1.5,
        sfx: 'slam',
        weight: 1.2,
      },
      {
        type: 'shoot',
        proj: P.seed,
        count: 7,
        spread: 1.3,
        bursts: 3,
        burstDelay: 0.35,
        windup: 0.7,
        recover: 0.8,
        cooldown: 4,
        range: 400,
        dmg: 1,
      },
      {
        type: 'ring',
        proj: { ...P.seed, speed: 95 },
        count: 16,
        waves: 2,
        waveDelay: 0.5,
        rotate: 0.2,
        windup: 0.8,
        recover: 0.8,
        cooldown: 5,
        range: 400,
        dmg: 1,
      },
      {
        type: 'summon',
        enemy: 'sapling',
        count: 2,
        max: 4,
        windup: 0.9,
        recover: 0.8,
        cooldown: 12,
        range: 400,
        dmg: 0,
        sfx: 'summon',
        weight: 0.6,
      },
      {
        type: 'teleport',
        near: 'player',
        novaRadius: 44,
        windup: 1.2,
        recover: 0.9,
        cooldown: 7,
        range: 400,
        dmg: 1.6,
        phase: 1,
        shout: 'The ground trembles!',
      },
    ],
    lore: 'Once a sacred tree at the heart of the woods. The Verdant Shard fell into its roots and it has been starving ever since.',
  },
  {
    id: 'crystal_golem',
    name: 'Crystal Colossus',
    sprite: 'boss_crystal_golem',
    hp: 1500,
    atk: 16,
    def: 16,
    speed: 30,
    radius: 18,
    mass: 1,
    xp: 360,
    gold: 500,
    behavior: 'boss',
    aggro: 999,
    element: 'earth',
    immune: ['freeze'],
    boss: {
      title: 'Warden of the Deep',
      music: 'boss',
      shardName: 'Prism Shard',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        { at: 0.5, speedMult: 1.25, cooldownMult: 0.75, shout: 'The crystals blaze with fury!' },
      ],
    },
    attacks: [
      {
        type: 'charge',
        speed: 320,
        length: 220,
        windup: 0.9,
        recover: 1,
        cooldown: 4,
        range: 260,
        minRange: 50,
        dmg: 1.7,
        sfx: 'roar',
      },
      { type: 'nova', radius: 62, windup: 1, recover: 0.8, cooldown: 3.5, range: 60, dmg: 1.6, sfx: 'slam' },
      {
        type: 'aoe',
        at: 'line',
        radius: 16,
        count: 8,
        spread: 26,
        delay: 0.6,
        stagger: 0.1,
        windup: 0.6,
        recover: 0.8,
        cooldown: 3.5,
        range: 260,
        dmg: 1.4,
        sfx: 'ice_shard',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 20,
        count: 6,
        spread: 70,
        delay: 1,
        stagger: 0.12,
        windup: 0.6,
        recover: 0.8,
        cooldown: 5,
        range: 400,
        dmg: 1.3,
      },
      {
        type: 'aoe',
        at: 'cross',
        radius: 16,
        count: 7,
        spread: 26,
        delay: 0.7,
        stagger: 0.08,
        windup: 0.8,
        recover: 0.9,
        cooldown: 5,
        range: 400,
        dmg: 1.4,
        phase: 1,
      },
      {
        type: 'shoot',
        proj: P.crystal,
        count: 9,
        spread: 2.2,
        windup: 0.7,
        recover: 0.7,
        cooldown: 4,
        range: 400,
        dmg: 1,
        phase: 1,
      },
    ],
    lore: 'The caverns’ ancient guardian, now a vessel for the Prism Shard’s rage.',
  },
  {
    id: 'ignis',
    name: 'Ignis',
    sprite: 'boss_ignis',
    hp: 1900,
    atk: 20,
    def: 20,
    speed: 44,
    radius: 20,
    mass: 1,
    xp: 460,
    gold: 900,
    behavior: 'boss',
    aggro: 999,
    element: 'fire',
    immune: ['burn', 'freeze'],
    boss: {
      title: 'Tyrant of Emberpeak',
      music: 'boss',
      shardName: 'Ember Shard',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        { at: 0.5, speedMult: 1.2, cooldownMult: 0.75, shout: 'Burn to ash, little spark!' },
      ],
    },
    attacks: [
      {
        type: 'cone',
        length: 130,
        arc: 0.7,
        duration: 1.4,
        sweep: 1.2,
        windup: 0.9,
        recover: 0.9,
        cooldown: 5,
        range: 150,
        dmg: 0.5,
        status: { kind: 'burn', duration: 3, power: 0.25 },
        sfx: 'fire_breath',
      },
      {
        type: 'shoot',
        proj: {
          ...P.fireball,
          speed: 110,
          radius: 6,
          scale: 1.6,
          explode: { radius: 26, dmg: 1.2 },
          puddle: { radius: 18, duration: 4, dmg: 0.4, status: { kind: 'burn', duration: 2, power: 0.2 } },
        },
        count: 3,
        spread: 0.7,
        windup: 0.8,
        recover: 0.8,
        cooldown: 4,
        range: 400,
        dmg: 1.2,
        sfx: 'fireball',
      },
      {
        type: 'dive',
        radius: 58,
        airTime: 2,
        windup: 0.6,
        recover: 1.1,
        cooldown: 8,
        range: 400,
        dmg: 2,
        sfx: 'roar',
        shout: 'Ignis takes flight!',
      },
      {
        type: 'melee',
        arc: 3.4,
        reach: 46,
        windup: 0.6,
        recover: 0.7,
        cooldown: 2.5,
        range: 46,
        dmg: 1.5,
        sfx: 'swing_heavy',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 22,
        count: 5,
        spread: 80,
        delay: 1.1,
        stagger: 0.15,
        persist: 5,
        windup: 0.6,
        recover: 0.8,
        cooldown: 7,
        range: 400,
        dmg: 0.6,
        phase: 1,
        status: { kind: 'burn', duration: 2, power: 0.25 },
      },
    ],
    lore: 'A drake who swallowed the Ember Shard whole. The mountain has not stopped erupting since.',
  },
  {
    id: 'seraphine',
    name: 'Seraphine',
    sprite: 'boss_seraphine',
    hp: 2100,
    atk: 24,
    def: 22,
    speed: 60,
    radius: 10,
    mass: 1,
    xp: 550,
    gold: 1500,
    behavior: 'boss',
    aggro: 999,
    element: 'ice',
    immune: ['freeze', 'slow'],
    boss: {
      title: 'The Frost Queen',
      music: 'boss',
      portrait: 'seraphine',
      shardName: 'Rime Shard',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.55,
          speedMult: 1.2,
          cooldownMult: 0.7,
          shout: 'Why won’t you stay down?!',
          summon: { enemy: 'frost_wraith', count: 2 },
        },
      ],
    },
    attacks: [
      {
        type: 'shoot',
        proj: { ...P.iceshard, speed: 170 },
        count: 5,
        spread: 0.9,
        bursts: 2,
        burstDelay: 0.3,
        windup: 0.6,
        recover: 0.5,
        cooldown: 2.5,
        range: 400,
        dmg: 1,
        sfx: 'ice_shard',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 26,
        count: 3,
        spread: 60,
        delay: 1.1,
        persist: 4,
        windup: 0.6,
        recover: 0.6,
        cooldown: 6,
        range: 400,
        dmg: 0.5,
        status: { kind: 'slow', duration: 1, power: 0.45 },
        sfx: 'frost_nova',
      },
      {
        type: 'teleport',
        near: 'player',
        novaRadius: 48,
        windup: 0.9,
        recover: 0.8,
        cooldown: 6,
        range: 400,
        dmg: 1.4,
        status: { kind: 'freeze', duration: 1.2 },
      },
      {
        type: 'ring',
        proj: { ...P.iceshard, speed: 110 },
        count: 20,
        waves: 3,
        waveDelay: 0.45,
        rotate: 0.16,
        windup: 0.8,
        recover: 0.8,
        cooldown: 6,
        range: 400,
        dmg: 1,
      },
      {
        type: 'spiral',
        proj: { ...P.iceshard, speed: 105 },
        arms: 4,
        shots: 18,
        interval: 0.1,
        turn: 0.22,
        windup: 0.8,
        recover: 1,
        cooldown: 7,
        range: 400,
        dmg: 0.9,
        phase: 1,
      },
      {
        type: 'summon',
        enemy: 'frost_wraith',
        count: 1,
        max: 2,
        windup: 0.8,
        recover: 0.6,
        cooldown: 14,
        range: 400,
        dmg: 0,
        phase: 1,
        sfx: 'summon',
      },
    ],
    lore: 'Once the Order of Stars’ finest knight and the Rime Shard’s chosen. The cold took her heart first.',
  },
  {
    id: 'malachar',
    name: 'Malachar',
    sprite: 'boss_malachar',
    hp: 2300,
    atk: 28,
    def: 26,
    speed: 70,
    radius: 10,
    mass: 1,
    xp: 350,
    gold: 1500,
    behavior: 'boss',
    aggro: 999,
    element: 'void',
    immune: ['freeze'],
    boss: {
      title: 'The Hollow King',
      music: 'final_boss',
      portrait: 'malachar',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.5,
          speedMult: 1.2,
          cooldownMult: 0.75,
          shout: 'Kneel before the void!',
          summon: { enemy: 'shadow_knight', count: 2 },
        },
      ],
    },
    attacks: [
      {
        type: 'lunge',
        speed: 300,
        duration: 0.28,
        windup: 0.5,
        recover: 0.5,
        cooldown: 3,
        range: 160,
        dmg: 1.4,
        repeat: 3,
        sfx: 'dash_slash',
      },
      {
        type: 'melee',
        arc: 3,
        reach: 36,
        windup: 0.5,
        recover: 0.6,
        cooldown: 2,
        range: 38,
        dmg: 1.5,
        repeat: 2,
        sfx: 'swing_heavy',
      },
      {
        type: 'shoot',
        proj: { ...P.void, speed: 80 },
        count: 3,
        spread: 1.4,
        windup: 0.7,
        recover: 0.6,
        cooldown: 4,
        range: 400,
        dmg: 1.2,
        sfx: 'void_pulse',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 24,
        count: 4,
        spread: 60,
        delay: 1,
        stagger: 0.12,
        windup: 0.6,
        recover: 0.6,
        cooldown: 4.5,
        range: 400,
        dmg: 1.4,
        sfx: 'void_pulse',
      },
      {
        type: 'ring',
        proj: { ...P.magic, speed: 100 },
        count: 24,
        waves: 2,
        waveDelay: 0.5,
        rotate: 0.13,
        windup: 0.8,
        recover: 0.8,
        cooldown: 6,
        range: 400,
        dmg: 1,
        phase: 1,
      },
    ],
    lore: 'The Hollow King. He shattered the Aether Crystal to fill the emptiness where his heart once was.',
  },
  {
    id: 'malachar_true',
    name: 'Malachar, Void Incarnate',
    sprite: 'boss_malachar_true',
    hp: 2600,
    atk: 30,
    def: 26,
    speed: 30,
    radius: 22,
    mass: 1,
    xp: 700,
    gold: 3000,
    behavior: 'boss',
    aggro: 999,
    element: 'void',
    immune: ['freeze', 'slow'],
    boss: {
      title: 'The End of All Light',
      music: 'final_boss',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.4,
          speedMult: 1.3,
          cooldownMult: 0.7,
          shout: 'THERE IS NOTHING AFTER ME!',
          summon: { enemy: 'hollow_wisp', count: 4 },
        },
      ],
    },
    attacks: [
      {
        type: 'beam',
        length: 260,
        width: 16,
        duration: 2.4,
        sweep: 2.2,
        windup: 1,
        recover: 1,
        cooldown: 7,
        range: 400,
        dmg: 0.6,
        sfx: 'void_pulse',
      },
      {
        type: 'spiral',
        proj: { ...P.void, homing: 0, speed: 95, radius: 4 },
        arms: 5,
        shots: 20,
        interval: 0.1,
        turn: 0.17,
        windup: 0.8,
        recover: 1,
        cooldown: 7,
        range: 400,
        dmg: 1,
      },
      {
        type: 'aoe',
        at: 'around',
        radius: 26,
        count: 8,
        spread: 90,
        delay: 0.9,
        stagger: 0.06,
        windup: 0.6,
        recover: 0.7,
        cooldown: 4,
        range: 400,
        dmg: 1.5,
        sfx: 'slam',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 22,
        count: 5,
        spread: 70,
        delay: 1,
        stagger: 0.15,
        windup: 0.5,
        recover: 0.6,
        cooldown: 4,
        range: 400,
        dmg: 1.3,
      },
      {
        type: 'summon',
        enemy: 'hollow_wisp',
        count: 3,
        max: 5,
        windup: 0.8,
        recover: 0.6,
        cooldown: 10,
        range: 400,
        dmg: 0,
        sfx: 'summon',
      },
      {
        type: 'ring',
        proj: { ...P.magic, speed: 120 },
        count: 28,
        waves: 3,
        waveDelay: 0.4,
        rotate: 0.11,
        windup: 0.8,
        recover: 0.8,
        cooldown: 6,
        range: 400,
        dmg: 1,
        phase: 1,
      },
    ],
    lore: 'What remains when a king gives everything to the void. Only the Aether can end this.',
  },
];

// ------------------------------------------------------- world bosses ----

/** A world boss built on another monster's art and moves: bigger, tougher, never wandering far. */
function worldBoss(
  base: string,
  o: Partial<EnemyDef> & Pick<EnemyDef, 'id' | 'name' | 'lore' | 'boss'>,
): EnemyDef {
  const b = E.find((e) => e.id === base)!;
  return { ...b, aggro: 170, drops: [], ...o };
}

/** Roaming world bosses that appear during world boss events, one per zone. */
export const WORLD_BOSS_DEFS: readonly EnemyDef[] = [
  worldBoss('thornmaw', {
    id: 'wb_blightroot',
    name: 'Blightroot',
    hp: 3200,
    atk: 15,
    def: 12,
    scale: 1.3,
    xp: 1400,
    gold: 1200,
    boss: {
      title: 'World Boss • The Undying Bloom',
      music: 'boss',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.66,
          speedMult: 1,
          cooldownMult: 0.8,
          shout: 'The forest HUNGERS!',
          summon: { enemy: 'sapling', count: 4 },
        },
        {
          at: 0.33,
          speedMult: 1,
          cooldownMult: 0.6,
          shout: 'ROOTS, RISE!',
          summon: { enemy: 'mushroom', count: 3 },
        },
      ],
    },
    lore: 'A cutting of Thornmaw that took root elsewhere and never stopped growing. It wanders the woods when the shards stir.',
  }),
  worldBoss('crystal_golem', {
    id: 'wb_prismatic',
    name: 'Prismatic Colossus',
    hp: 4000,
    atk: 18,
    def: 24,
    scale: 1.25,
    xp: 1800,
    gold: 1600,
    boss: {
      title: 'World Boss • Heart of Every Crystal',
      music: 'boss',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.6,
          speedMult: 1.15,
          cooldownMult: 0.75,
          shout: 'The caverns sing for me!',
          summon: { enemy: 'shardling', count: 4 },
        },
        { at: 0.25, speedMult: 1.3, cooldownMult: 0.6, shout: 'SHATTER!' },
      ],
    },
    lore: 'Every crystal in the caverns hums with a fragment of its song. Sometimes the song walks.',
  }),
  worldBoss('ignis', {
    id: 'wb_pyrrhus',
    name: 'Pyrrhus the Cinderwyrm',
    hp: 4600,
    atk: 22,
    def: 22,
    scale: 1.2,
    xp: 2300,
    gold: 2200,
    boss: {
      title: 'World Boss • Sire of Ignis',
      music: 'boss',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        { at: 0.6, speedMult: 1.2, cooldownMult: 0.75, shout: 'My son was a candle. I am the SUN.' },
        {
          at: 0.3,
          speedMult: 1.3,
          cooldownMult: 0.6,
          shout: 'BURN!',
          summon: { enemy: 'fire_imp', count: 4 },
        },
      ],
    },
    lore: 'The ancient drake who raised Ignis. It sleeps under the lava lakes and wakes to hunt.',
  }),
  worldBoss('yeti', {
    id: 'wb_grimfrost',
    name: 'Old Grimfrost',
    hp: 4400,
    atk: 30,
    def: 26,
    speed: 40,
    radius: 9,
    mass: 1,
    scale: 2.3,
    xp: 2800,
    gold: 2600,
    immune: ['freeze', 'slow'],
    attacks: [
      {
        type: 'melee',
        arc: 3,
        reach: 48,
        windup: 0.7,
        recover: 0.6,
        cooldown: 1.6,
        range: 52,
        dmg: 1.8,
        repeat: 1,
        sfx: 'swing_heavy',
      },
      { type: 'nova', radius: 70, windup: 1, recover: 0.8, cooldown: 6, range: 70, dmg: 2, sfx: 'slam' },
      {
        type: 'shoot',
        proj: { ...P.snowball, scale: 2, radius: 7 },
        count: 5,
        spread: 0.8,
        windup: 0.8,
        recover: 0.6,
        cooldown: 3.5,
        range: 220,
        minRange: 60,
        dmg: 1.2,
      },
      {
        type: 'ring',
        proj: P.iceshard,
        count: 18,
        waves: 2,
        waveDelay: 0.45,
        rotate: 0.17,
        windup: 0.9,
        recover: 0.8,
        cooldown: 7,
        range: 300,
        dmg: 1,
        phase: 1,
        shout: 'RAAAWR!',
      },
      {
        type: 'summon',
        enemy: 'wolf_ice',
        count: 3,
        max: 5,
        windup: 1,
        recover: 0.8,
        cooldown: 14,
        range: 300,
        dmg: 0,
        sfx: 'summon',
        weight: 0.6,
      },
    ],
    boss: {
      title: 'World Boss • King of the Frostveil',
      music: 'boss',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        { at: 0.5, speedMult: 1.25, cooldownMult: 0.7, shout: 'GRIMFROST SMASH!' },
      ],
    },
    lore: 'The oldest yeti alive. The pack follows its howl, and so do the blizzards.',
  }),
  worldBoss('shadow_knight', {
    id: 'wb_warlord',
    name: 'The Hollow Warlord',
    hp: 5200,
    atk: 34,
    def: 30,
    speed: 48,
    radius: 8,
    mass: 1,
    scale: 2.2,
    guard: 0.5,
    xp: 3400,
    gold: 3200,
    attacks: [
      {
        type: 'melee',
        arc: 2.6,
        reach: 44,
        windup: 0.5,
        recover: 0.5,
        cooldown: 1.5,
        range: 46,
        dmg: 1.4,
        repeat: 2,
        sfx: 'swing_heavy',
      },
      {
        type: 'lunge',
        speed: 300,
        duration: 0.35,
        windup: 0.6,
        recover: 0.6,
        cooldown: 3.5,
        range: 140,
        minRange: 50,
        dmg: 1.5,
      },
      {
        type: 'spiral',
        proj: P.void,
        arms: 4,
        shots: 10,
        interval: 0.15,
        turn: 0.3,
        windup: 0.9,
        recover: 0.8,
        cooldown: 8,
        range: 300,
        dmg: 1,
        phase: 1,
        shout: 'Kneel before the Void!',
      },
      { type: 'nova', radius: 64, windup: 0.9, recover: 0.8, cooldown: 6, range: 64, dmg: 1.8, sfx: 'slam' },
      {
        type: 'summon',
        enemy: 'void_mage',
        count: 2,
        max: 4,
        windup: 1,
        recover: 0.8,
        cooldown: 14,
        range: 300,
        dmg: 0,
        sfx: 'summon',
        weight: 0.6,
      },
    ],
    boss: {
      title: 'World Boss • First of the Hollow',
      music: 'boss',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        { at: 0.5, speedMult: 1.2, cooldownMult: 0.7, shout: 'My king shall rise again!' },
      ],
    },
    lore: 'The first knight to give his heart to Malachar. The armor still marches, looking for its king.',
  }),
];

/** Which world boss haunts each zone. */
export const WORLD_BOSS_BY_ZONE: Readonly<Record<string, string>> = {
  forest: 'wb_blightroot',
  cave: 'wb_prismatic',
  volcano: 'wb_pyrrhus',
  tundra: 'wb_grimfrost',
  citadel: 'wb_warlord',
};

export const ENEMIES: Readonly<Record<string, EnemyDef>> = Object.fromEntries(
  [...E, ...ACT2_ENEMIES, ...WORLD_BOSS_DEFS].map((e) => [e.id, e]),
);

export function enemyDef(id: string): EnemyDef {
  const d = ENEMIES[id];
  if (!d) throw new Error(`Unknown enemy ${id}`);
  return d;
}

/** Elite modifiers ("champions") randomly applied to regular enemies. */
export interface EliteMod {
  id: string;
  prefix: string;
  color: string;
  desc: string;
}

export const ELITE_MODS: readonly EliteMod[] = [
  { id: 'frenzied', prefix: 'Frenzied', color: '#e43b44', desc: 'Moves and attacks faster.' },
  { id: 'armored', prefix: 'Armored', color: '#c0cbdc', desc: 'Greatly increased defense.' },
  { id: 'vampiric', prefix: 'Vampiric', color: '#b55088', desc: 'Heals when dealing damage.' },
  { id: 'blazing', prefix: 'Blazing', color: '#f77622', desc: 'Leaves fire in its wake.' },
  { id: 'giant', prefix: 'Giant', color: '#63c74d', desc: 'Huge, with double health.' },
  { id: 'shielded', prefix: 'Warded', color: '#2ce8f5', desc: 'Periodically shields itself.' },
];
